import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, AreaChart, Area } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { TrendingUp, TrendingDown, Clock, CalendarDays, Target } from 'lucide-react';

const StatisticsPanel = ({ timeReports, personalReport, allEmployeesReport, user }) => {
  const isManager = user?.role === 'manager' || user?.role === 'admin';
  
  // Process daily summaries for charts
  const processWeeklyData = () => {
    if (!personalReport?.time_entries) return [];
    
    const entries = personalReport.time_entries;
    const weeklyData = {};
    
    entries.forEach(entry => {
      const date = new Date(entry.date);
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay() + 1); // Monday
      const weekKey = weekStart.toISOString().split('T')[0];
      
      if (!weeklyData[weekKey]) {
        weeklyData[weekKey] = { week: weekKey, hours: 0, days: 0 };
      }
      weeklyData[weekKey].hours += entry.total_hours || 0;
      weeklyData[weekKey].days += 1;
    });
    
    return Object.values(weeklyData)
      .sort((a, b) => new Date(a.week) - new Date(b.week))
      .slice(-8)
      .map(w => ({
        ...w,
        weekLabel: `Sem ${new Date(w.week).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}`,
        hours: Math.round(w.hours * 100) / 100
      }));
  };

  const processMonthlyTrend = () => {
    if (!personalReport?.monthly_summary) return [];
    
    return personalReport.monthly_summary.map(month => ({
      month: month.month_name,
      hours: Math.round(month.total_hours * 100) / 100,
      days: month.working_days,
      avg: month.working_days > 0 ? Math.round((month.total_hours / month.working_days) * 100) / 100 : 0
    }));
  };

  const processDailyData = () => {
    if (!timeReports?.daily_summaries) return [];
    
    return timeReports.daily_summaries
      .slice(0, 14)
      .reverse()
      .map(day => ({
        date: new Date(day.date).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }),
        hours: Math.round(day.total_hours * 100) / 100,
        entries: day.entries
      }));
  };

  const weeklyData = processWeeklyData();
  const monthlyTrend = processMonthlyTrend();
  const dailyData = processDailyData();

  // Calculate stats
  const avgDailyHours = timeReports?.average_daily_hours || 0;
  const targetHours = 8;
  const performance = avgDailyHours > 0 ? Math.round((avgDailyHours / targetHours) * 100) : 0;
  const trend = avgDailyHours >= targetHours ? 'up' : 'down';

  return (
    <div className="space-y-6">
      {/* Summary Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Horas Totales (30d)</p>
                <p className="text-2xl font-bold">{timeReports?.total_hours_30_days || 0}h</p>
              </div>
              <Clock className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Días Trabajados</p>
                <p className="text-2xl font-bold">{timeReports?.days_worked || 0}</p>
              </div>
              <CalendarDays className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Promedio Diario</p>
                <p className="text-2xl font-bold">{avgDailyHours}h</p>
              </div>
              {trend === 'up' ? (
                <TrendingUp className="h-8 w-8 text-green-500" />
              ) : (
                <TrendingDown className="h-8 w-8 text-orange-500" />
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Rendimiento</p>
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-bold">{performance}%</p>
                  <Badge variant={performance >= 100 ? "default" : "secondary"}>
                    {performance >= 100 ? 'Objetivo' : 'Bajo objetivo'}
                  </Badge>
                </div>
              </div>
              <Target className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Weekly Hours Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Horas por Semana</CardTitle>
          </CardHeader>
          <CardContent>
            {weeklyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={weeklyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="weekLabel" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    formatter={(value) => [`${value}h`, 'Horas']}
                    labelFormatter={(label) => `Semana: ${label}`}
                  />
                  <Bar dataKey="hours" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-slate-500">
                No hay datos suficientes
              </div>
            )}
          </CardContent>
        </Card>

        {/* Daily Hours Trend */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Horas Diarias (últimos 14 días)</CardTitle>
          </CardHeader>
          <CardContent>
            {dailyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={dailyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 12 }} domain={[0, 12]} />
                  <Tooltip 
                    formatter={(value) => [`${value}h`, 'Horas']}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="hours" 
                    stroke="#10b981" 
                    fill="#d1fae5" 
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-slate-500">
                No hay datos suficientes
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Monthly Trend */}
      {monthlyTrend.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Tendencia Mensual</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthlyTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Line 
                  yAxisId="left"
                  type="monotone" 
                  dataKey="hours" 
                  stroke="#3b82f6" 
                  strokeWidth={2}
                  name="Horas Totales"
                  dot={{ fill: '#3b82f6' }}
                />
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="avg" 
                  stroke="#10b981" 
                  strokeWidth={2}
                  name="Promedio Diario"
                  dot={{ fill: '#10b981' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Manager: Team Overview */}
      {isManager && allEmployeesReport && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Resumen del Equipo (30 días)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart 
                data={allEmployeesReport.employee_summaries?.slice(0, 10) || []}
                layout="vertical"
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis 
                  type="category" 
                  dataKey="user_name" 
                  tick={{ fontSize: 11 }} 
                  width={120}
                />
                <Tooltip 
                  formatter={(value, name) => [
                    name === 'total_hours' ? `${value}h` : value,
                    name === 'total_hours' ? 'Horas' : 'Días'
                  ]}
                />
                <Legend />
                <Bar dataKey="total_hours" fill="#3b82f6" name="Horas Totales" />
                <Bar dataKey="total_days_worked" fill="#10b981" name="Días Trabajados" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default StatisticsPanel;
